package net.bothosting.docs.components.layouts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import net.bothosting.docs.components.ui.BrandText
import net.bothosting.docs.components.widgets.Breadcrumb
import net.bothosting.docs.components.widgets.Sidebar
import net.bothosting.docs.components.widgets.SidebarTheme
import net.bothosting.docs.components.widgets.TableOfContents
import net.bothosting.docs.toSitePalette
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Span

private val SIDEBAR_WIDTH = SidebarTheme.sidebarWidth

@Layout
@Composable
fun DocsLayout(content: @Composable () -> Unit) {
    val sitePalette = ColorMode.current.toSitePalette()
    val textColor = ColorMode.current.toPalette().color
    var windowWidth by remember { mutableStateOf(0) }
    var sidebarOpen by remember { mutableStateOf(false) }

    DisposableEffect(Unit) {
        windowWidth = kotlinx.browser.window.innerWidth
        val onResize: (org.w3c.dom.events.Event) -> Unit = { windowWidth = kotlinx.browser.window.innerWidth }
        kotlinx.browser.window.addEventListener("resize", onResize)
        onDispose { kotlinx.browser.window.removeEventListener("resize", onResize) }
    }

    val isWide = windowWidth >= 1024

    if (isWide) {
        // ── Wide layout: fixed topbar + 3 independent columns ───
        Box(
            Modifier
                .fillMaxSize()
                .backgroundColor(Color.rgb(0x020408))
        ) {
            TopBar(textColor, sitePalette, showHamburger = false, onToggleSidebar = {})

            Row(
                Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("position", "absolute")
                        property("top", "55px")
                        property("bottom", "0")
                    }
            ) {
                Sidebar()
                ContentArea(content, rightContent = { TableOfContents() })
            }
        }
    } else {
        // ── Narrow layout: sidebar overlays content ───────────────
        Box(
            Modifier
                .fillMaxWidth()
                .minHeight(100.vh)
                .backgroundColor(Color.rgb(0x020408))
        ) {
            TopBar(textColor, sitePalette, showHamburger = true, onToggleSidebar = { sidebarOpen = !sidebarOpen })

            Column(
                Modifier
                    .fillMaxWidth()
                    .padding(top = 55.px)
            ) {
                ContentArea(content)
            }

            // backdrop
            if (sidebarOpen) {
                Box(
                    Modifier
                        .fillMaxSize()
                        .position(Position.Fixed)
                        .top(0.px)
                        .left(0.px)
                        .zIndex(999)
                        .backgroundColor(Color.argb(0x66000000))
                        .onClick { sidebarOpen = false }
                )
            }

            // sidebar overlay (slides from left)
            Box(
                Modifier
                    .position(Position.Fixed)
                    .top(0.px)
                    .left(0.px)
                    .height(100.vh)
                    .zIndex(1000)
                    .styleModifier {
                        property("transform", if (sidebarOpen) "translateX(0)" else "translateX(-100%)")
                        property("transition", "transform 0.25s ease")
                    }
            ) {
                Sidebar()
            }
        }
    }
}

@Composable
private fun TopBar(
    textColor: com.varabyte.kobweb.compose.ui.graphics.Color,
    sitePalette: net.bothosting.docs.SitePalette,
    showHamburger: Boolean,
    onToggleSidebar: () -> Unit,
) {
    Row(
        Modifier
            .fillMaxWidth()
            .height(55.px)
            .position(Position.Fixed)
            .top(0.px)
            .zIndex(10)
            .backgroundColor(Color.rgb(0x020304))
            .borderBottom(1.px, LineStyle.Solid, Color.argb(0x661a1f2e)),
        verticalAlignment = Alignment.CenterVertically
    ) {
        if (showHamburger) {
            Row(
                Modifier
                    .fillMaxWidth()
                    .padding(left = 0.75.cssRem, right = 1.5.cssRem),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(
                    Modifier
                        .width(1.75.cssRem)
                        .height(1.75.cssRem)
                        .onClick { onToggleSidebar() }
                        .styleModifier {
                            property("cursor", "pointer")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                        }
                ) {
                    Span(
                        Modifier
                            .fontSize(1.1.cssRem)
                            .color(textColor)
                            .toAttrs { attr("class", "fas fa-bars") }
                    )
                }
                Box(Modifier.width(0.75.cssRem))

                SpanText(
                    "Documentation",
                    Modifier
                        .fontWeight(FontWeight.SemiBold)
                        .fontSize(1.cssRem)
                        .color(textColor)
                )

                Box(Modifier.flexGrow(1))

                Row(Modifier.gap(0.5.cssRem), verticalAlignment = Alignment.CenterVertically) {
                    TopBarIconLink("https://bot-hosting.net", "fa-arrow-right-to-bracket", "bot-hosting.net")
                    TopBarIconLink("https://discord.com/invite/6FKKj4qEM6", "fa-brands fa-discord", "Discord")
                }
            }
        } else {
            // Logo area — same width as sidebar
            Box(
                Modifier
                    .width(SIDEBAR_WIDTH)
                    .flexShrink(0)
                    .fillMaxHeight()
                    .borderRight(1.px, LineStyle.Solid, Color.argb(0x661a1f2e))
            ) {
                Link(
                    "/",
                    variant = UndecoratedLinkVariant,
                    modifier = Modifier.fillMaxSize()
                ) {
                    Row(
                        Modifier
                            .fillMaxSize()
                            .padding(left = 0.75.cssRem),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Image(
                            src = "/bothosting.png",
                            modifier = Modifier
                                .width(SidebarTheme.logoImageSize)
                                .height(SidebarTheme.logoImageSize)
                                .borderRadius(SidebarTheme.logoImageRadius)
                        )
                        Box(Modifier.width(SidebarTheme.logoGap))
                        BrandText()
                    }
                }
            }

            // Content area header
            Row(
                Modifier
                    .fillMaxWidth()
                    .padding(left = 56.px, right = 1.5.cssRem),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    "Documentation",
                    Modifier
                        .fontWeight(FontWeight.Bold)
                        .fontSize(1.15.cssRem)
                        .color(textColor)
                )

                Box(Modifier.flexGrow(1))

                Row(Modifier.gap(0.5.cssRem), verticalAlignment = Alignment.CenterVertically) {
                    TopBarIconLink("https://bot-hosting.net", "fa-arrow-right-to-bracket", "bot-hosting.net")
                    TopBarIconLink("https://discord.com/invite/6FKKj4qEM6", "fa-brands fa-discord", "Discord")
                }
            }
        }
    }
}

@Composable
private fun ContentArea(
    content: @Composable () -> Unit,
    rightContent: @Composable () -> Unit = {},
) {
    val context = rememberPageContext()
    val isHomePage = context.route.path == "/" || context.route.path.isEmpty()

    Box(
        Modifier
            .fillMaxHeight()
            .styleModifier { property("flex", "1 1 0%") }
            .overflow(Overflow.Auto)
    ) {
        Row(
            Modifier
                .fillMaxWidth()
                .padding(left = 56.px, top = 2.cssRem, right = 2.cssRem, bottom = 3.cssRem)
                .styleModifier {
                    property("margin-left", "auto")
                    property("margin-right", "auto")
                }
                .widthIn(max = 1100.px)
        ) {
            Column(
                Modifier
                    .weight(1f)
                    .classNames("content-area")
            ) {
                if (!isHomePage) Breadcrumb()
                content()
            }

            rightContent()
        }

        LaunchedEffect(Unit) {
            fun addAnchors() {
                val doc = kotlinx.browser.document
                val headings = doc.querySelectorAll(".content-area h1, .content-area h2, .content-area h3, .content-area h4")
                for (i in 0 until headings.length) {
                    val heading = headings.item(i) as? org.w3c.dom.HTMLElement ?: continue
                    val id = heading.id
                    if (id.isNotEmpty() && heading.querySelector(".header-anchor") == null) {
                        val anchor = doc.createElement("a") as org.w3c.dom.HTMLAnchorElement
                        anchor.className = "header-anchor"
                        anchor.href = "#$id"
                        anchor.textContent = "#"
                        anchor.style.apply {
                            position = "absolute"
                            left = "-1.2em"
                            top = "0"
                            width = "1.2em"
                            height = "100%"
                            display = "flex"
                            alignItems = "center"
                            fontSize = "0.75em"
                            fontWeight = "normal"
                            textDecoration = "none"
                            transition = "color 0.15s ease"
                        }
                        heading.prepend(anchor)
                    }
                }
            }
            addAnchors()

            val contentArea = kotlinx.browser.document.querySelector(".content-area") as? org.w3c.dom.Node
            if (contentArea != null) {
                val observer = org.w3c.dom.MutationObserver { _, _ -> addAnchors() }
                observer.observe(contentArea, org.w3c.dom.MutationObserverInit(childList = true, subtree = true))
            }
        }
    }
}

@Composable
private fun TopBarIconLink(href: String, icon: String, label: String) {
    val sitePalette = ColorMode.current.toSitePalette()
    Link(href, variant = UndecoratedLinkVariant) {
        Row(
            Modifier
                .gap(0.35.cssRem)
                .backgroundColor(Color.argb(0x141a1f2e))
                .padding(leftRight = 0.65.cssRem, topBottom = 0.4.cssRem)
                .borderRadius(0.4.cssRem)
                .color(sitePalette.sidebarText)
                .fontSize(0.8.cssRem),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Span(
                Modifier
                    .fontSize(0.75.cssRem)
                    .color(sitePalette.sidebarIcon)
                    .toAttrs { attr("class", "fas $icon") }
            )
            SpanText(label, Modifier.fontSize(0.8.cssRem).color(sitePalette.sidebarText))
        }
    }
}
